import { EmbedStore } from '@/store'
import { AtlasEmbed, Embed, PassivesEmbed, TreeNote } from '@/types/embed'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React from 'react'
import { RenderMarkdown } from '../MarkdownEditor'
import { NoteEditor } from './NoteEditor'
import { FocusAction } from './common'
import { PassiveSidebar } from './sidebar'

function PassiveNote({
  note,
  setFocus,
  onEdit,
  onDelete,
  editing
}: {
  note: TreeNote
  setFocus: (action: FocusAction | undefined) => void
  onEdit?: () => void
  onDelete?: () => void
  editing?: boolean
}) {
  const onMouseEnter = React.useCallback(
    () => setFocus({ nodes: note.nodes }),
    [note.nodes, setFocus]
  )
  const onMouseLeave = React.useCallback(() => setFocus(undefined), [setFocus])
  const onClick = React.useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('.poe-buttons')) return
      if (note.nodes.length) setFocus({ nodes: note.nodes, center: true })
    },
    [note.nodes, setFocus]
  )
  if (!onEdit && !note.text) return null
  return (
    <div
      className={classNames('poe-note poe-Markdown', {
        'poe-editing': editing
      })}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}>
      {!!onEdit && (
        <div className="poe-buttons">
          <FontAwesomeIcon icon={faEdit} onClick={onEdit} />
          {!!onDelete && (
            <FontAwesomeIcon icon={faTrashAlt} onClick={onDelete} />
          )}
        </div>
      )}
      {note.text ? (
        <RenderMarkdown value={note.text} />
      ) : (
        <div className="poe-empty">Empty Note</div>
      )}
    </div>
  )
}

export function PassiveNotes({
  embed,
  onChange,
  setFocus
}: {
  embed: PassivesEmbed | AtlasEmbed
  onChange?: (value: Embed | ((prev: Embed) => Embed)) => void
  setFocus: (action: FocusAction | undefined) => void
}) {
  const [editing, setEditing] = React.useState(-1)
  const variant =
    embed.variants[embed.active] ??
    embed.variants[0] ??
    EmbedStore.passives.variants[0]
  const onCreate = React.useCallback(() => {
    if (!onChange) return
    onChange((embed) => {
      if (!('variants' in embed)) return embed
      embed = { ...embed }
      embed.variants = embed.variants.slice()
      const index = embed.variants[embed.active] ? embed.active : 0
      const variant = (embed.variants[index] = { ...embed.variants[index] })
      variant.notes = variant.notes?.slice() || []
      variant.notes.push({ text: '', nodes: [] })
      return embed
    })
    setEditing(variant.notes?.length || 0)
  }, [variant.notes, onChange])
  const onDelete = React.useCallback(
    (noteIndex: number) => {
      if (!onChange) return
      onChange((embed) => {
        if (!('variants' in embed)) return embed
        embed = { ...embed }
        embed.variants = embed.variants.slice()
        const index = embed.variants[embed.active] ? embed.active : 0
        const variant = (embed.variants[index] = { ...embed.variants[index] })
        if (variant.notes) {
          variant.notes = variant.notes.slice()
          variant.notes.splice(noteIndex, 1)
        }
        return embed
      })
      setEditing((editing) => {
        if (editing === noteIndex) return -1
        if (editing > noteIndex) return editing - 1
        return editing
      })
    },
    [onChange]
  )
  const onChangeNote = React.useCallback(
    (value: TreeNote | ((prev: TreeNote) => TreeNote)) =>
      onChange?.((embed) => {
        if (!('variants' in embed)) return embed
        const result = { ...embed }
        result.variants = embed.variants.slice()
        const index = embed.variants[embed.active] ? embed.active : 0
        const variant = (result.variants[index] = { ...result.variants[index] })
        const prev = variant.notes?.[editing]
        if (!prev) return embed
        if (typeof value === 'function') value = value(prev)
        if (value === prev) return embed
        variant.notes = variant.notes.slice()
        variant.notes[editing] = value
        return result
      }),
    [onChange, editing]
  )
  if (!onChange && !variant.notes?.length) return null
  return (
    <PassiveSidebar className="poe-notes" title="Notes">
      {!!variant.notes &&
        variant.notes.map((note, index) => (
          <React.Fragment key={index}>
            <PassiveNote
              editing={editing === index}
              note={note}
              setFocus={setFocus}
              onEdit={onChange ? () => setEditing(index) : undefined}
              onDelete={onChange ? () => onDelete(index) : undefined}
            />
          </React.Fragment>
        ))}
      {!!onChange && (
        <div className="poe-note poe-add" onClick={onCreate}>
          Create Note
        </div>
      )}
      {!!variant.notes?.[editing] && (
        <NoteEditor
          note={variant.notes[editing]}
          onChange={onChangeNote}
          onClose={() => setEditing(-1)}
          setFocus={setFocus}
        />
      )}
    </PassiveSidebar>
  )
}
